// src/components/Navbar.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Typography,
  Stack,
  Button,
  SwipeableDrawer,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  Box,
  ListItemIcon,
  Link as MuiLink,
  Popper,
  alpha,
  Paper,
} from '@mui/material';
import HandshakeIcon from '@mui/icons-material/Handshake';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, LayoutGroup, AnimatePresence } from "framer-motion";

// Custom Off-White & Brown Palette (Espresso, Warm Mocha, Cream)
const BROWN_THEME_PALETTE = {
  primary: "#3E2723", // Dark Espresso Brown
  secondary: "#6E5B4D", // Muted Warm Brown/Taupe
  accent: "#A06E50", // Caramel / Warm Accent
  background: "#FBF9F5", // Soft Off-White
  surface: "#FFFDF0", // Cream Container
  textPrimary: "#2C1D11",
  textSecondary: "#6E5B4D",
};

const M3_EXPRESSIVE_PALETTE = {
  amber: {
    accent: "#C19A6B",
    light: { bg: "#FAF3E0", text: "#3E2723", container: "#F2E8D0", onContainer: "#4E3629" },
  },
  emerald: {
    accent: "#8C7B6B",
    light: { bg: "#F2EDE4", text: "#2C1D11", container: "#E6DECF", onContainer: "#3E2723" },
  },
  purple: {
    accent: "#A67B5B",
    light: { bg: "#F4EDE6", text: "#3E2723", container: "#E8DDD3", onContainer: "#2C1D11" },
  },
};

const bounceSpring = {
  type: "spring",
  stiffness: 380,
  damping: 24,
  mass: 0.8,
};

const jigglySpring = {
  type: "spring",
  stiffness: 300,
  damping: 15,
  mass: 0.8,
};

// Water Pill Droplet Animation Variants
const waterDropMenuVariants = {
  hidden: {
    opacity: 0,
    scaleY: 0.35,
    scaleX: 0.75,
    y: -18,
    borderRadius: "999px",
    filter: "blur(8px)",
    transition: {
      duration: 0.18,
      ease: [0.4, 0, 1, 1],
    },
  },
  visible: {
    opacity: 1,
    scaleY: 1,
    scaleX: 1,
    y: 0,
    borderRadius: "26px",
    filter: "blur(0px)",
    transition: {
      type: "spring",
      stiffness: 340,
      damping: 18,
      mass: 0.85,
      delayChildren: 0.05,
      staggerChildren: 0.04,
    },
  },
  exit: {
    opacity: 0,
    scaleY: 0.45,
    scaleX: 0.82,
    y: -14,
    borderRadius: "999px",
    filter: "blur(6px)",
    transition: {
      duration: 0.15,
      ease: "easeInOut",
    },
  },
};

const waterDropItemVariants = {
  hidden: { opacity: 0, y: 10, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 380,
      damping: 20,
    },
  },
};

// Navigation Data Definition
const ABOUT_ITEMS = [
  { icon: '📖', title: 'Our Story', subtitle: 'Why we built WiYou!', mobileSub: 'Mission & founders', path: '/about', paletteKey: 'amber' },
  { icon: '🎧', title: 'Contact & Support', subtitle: '24/7 dedicated assistance', mobileSub: '24/7 squad help', path: '/contact', paletteKey: 'emerald' },
];

const Navbar = ({ user }) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [aboutAnchorEl, setAboutAnchorEl] = useState(null);
  const hoverTimeoutRef = useRef(null);

  const [bottomNavOpen, setBottomNavOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const isAboutActive = useMemo(() => {
    return ABOUT_ITEMS.some((i) => i.path === location.pathname);
  }, [location.pathname]);

  const activeDesktopNavId = useMemo(() => {
    if (location.pathname === '/') return 'home';
    if (location.pathname === '/projects') return 'services';
    if (location.pathname === '/pricing') return 'pricing';
    if (isAboutActive) return 'about';
    return null;
  }, [location.pathname, isAboutActive]);

  const handleMenuMouseEnter = (menuKey, targetEl) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    if (menuKey === 'about') setAboutAnchorEl(targetEl);
    setHoveredMenu(menuKey);
  };

  const handleMenuMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredMenu(null);
    }, 180);
  };

  const handleRouteNavigate = (path) => {
    setHoveredMenu(null);
    setBottomNavOpen(false);
    navigate(path);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const renderDesktopMegaPopper = (isOpen, anchorEl, title, subtitle, items, viewAllPath, menuKey) => (
    <Popper
      open={isOpen}
      anchorEl={anchorEl}
      placement="bottom"
      style={{ zIndex: 1200, pointerEvents: 'auto' }}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 14],
          },
        },
      ]}
      onMouseEnter={() => handleMenuMouseEnter(menuKey, anchorEl)}
      onMouseLeave={handleMenuMouseLeave}
    >
      <AnimatePresence>
        {isOpen && (
          <Box
            component={motion.div}
            variants={waterDropMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              transformOrigin: "top center",
              willChange: "transform, opacity, border-radius",
            }}
            sx={{
              p: 2.4,
              width: 440,
              backgroundColor: "rgba(255, 253, 250, 0.94)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              border: "1.5px solid rgba(139, 115, 85, 0.22)",
              boxShadow: '0 24px 48px -12px rgba(62, 39, 35, 0.16), 0 0 0 1px rgba(255, 255, 255, 0.6) inset',
              color: BROWN_THEME_PALETTE.textPrimary,
              overflow: 'hidden',
            }}
          >
            <Box
              component={motion.div}
              variants={waterDropItemVariants}
              onClick={() => handleRouteNavigate(viewAllPath)}
              sx={{
                px: 0.5,
                mb: 1.8,
                cursor: 'pointer',
                transition: 'opacity 0.2s ease',
                '&:hover': { opacity: 0.82 },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: BROWN_THEME_PALETTE.primary }}>
                {title}
              </Typography>
              <Typography variant="body2" sx={{ color: BROWN_THEME_PALETTE.textSecondary, mt: 0.3, fontSize: "0.86rem" }}>
                {subtitle}
              </Typography>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.2, mb: 1.8 }}>
              {items.map((item, idx) => {
                const isOptionActive = location.pathname === item.path;
                const palette = M3_EXPRESSIVE_PALETTE[item.paletteKey || 'amber'];
                const cardActiveBg = palette.light.container;
                const cardActiveText = palette.light.onContainer;
                const cardHoverBg = alpha(BROWN_THEME_PALETTE.accent, 0.12);

                return (
                  <Paper
                    key={idx}
                    component={motion.div}
                    variants={waterDropItemVariants}
                    whileHover={{ scale: 1.04, y: -2 }}
                    whileTap={{ scale: 0.96 }}
                    transition={jigglySpring}
                    onClick={() => handleRouteNavigate(item.path)}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: "16px",
                      backgroundColor: isOptionActive
                        ? cardActiveBg
                        : "rgba(139, 115, 85, 0.04)",
                      border: "1.5px solid",
                      borderColor: isOptionActive ? "rgba(139, 115, 85, 0.3)" : "rgba(139, 115, 85, 0.1)",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      color: isOptionActive ? cardActiveText : BROWN_THEME_PALETTE.textPrimary,
                      minHeight: 94,
                      transition: "background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        backgroundColor: isOptionActive ? palette.accent : cardHoverBg,
                        borderColor: BROWN_THEME_PALETTE.accent,
                        color: cardActiveText,
                        "& .item-subtitle": {
                          color: alpha(cardActiveText, 0.85),
                        },
                        "& .item-arrow": {
                          color: cardActiveText,
                        },
                      },
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: "1.2rem", mb: 0.4 }}>{item.icon}</Typography>
                      <Typography
                        sx={{
                          fontSize: "0.86rem",
                          fontWeight: isOptionActive ? 800 : 700,
                          lineHeight: 1.2,
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        className="item-subtitle"
                        sx={{
                          fontSize: "0.72rem",
                          color: BROWN_THEME_PALETTE.textSecondary,
                          mt: 0.3,
                          lineHeight: 1.3,
                          transition: "color 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        {item.subtitle}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.8 }}>
                      <ArrowForwardRoundedIcon
                        className="item-arrow"
                        sx={{
                          fontSize: 14,
                          color: BROWN_THEME_PALETTE.accent,
                          transition: "color 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      />
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Box>
        )}
      </AnimatePresence>
    </Popper>
  );

  return (
    <>
      <Box
        component="header"
        sx={{
          position: "fixed",
          top: { xs: 12, md: 16 },
          left: 0,
          right: 0,
          mx: "auto",
          width: { xs: "92%", md: "90%" },
          maxWidth: "1200px",
          zIndex: 1000,
          py: { xs: 0.6, md: 0.8 },
          px: { xs: 2, sm: 3 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(255, 253, 250, 0)",
          borderRadius: "999px",
          border: "none",
          boxShadow: "none",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Brand Logo */}
        <Box
          component={motion.div}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => handleRouteNavigate("/")}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            userSelect: "none",
          }}
        >
          <Box
            component={motion.div}
            layout
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              cursor: "pointer",
              backgroundColor: isScrolled
                ? "rgba(139, 115, 85, 0.17)"
                : "transparent",
              px: 0.5,
              py: 0.5,
              pr: isScrolled ? 0.5 : 1.5,
              borderRadius: "999px",
              transition: "background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s ease",
              backdropFilter: isScrolled ? "blur(16px)" : "none",
              WebkitBackdropFilter: isScrolled ? "blur(16px)" : "none",
            }}
          >
            <Box
              component="img"
              src="/assets/logo.png"
              alt="WiYou Logo"
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                objectFit: "contain",
              }}
            />

            <AnimatePresence mode="wait">
              {!isScrolled && (
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, scale: 0.85, width: 0, x: -8 }}
                  animate={{ opacity: 1, scale: 1, width: "auto", x: 0 }}
                  exit={{
                    opacity: 0,
                    scale: 0.8,
                    width: 0,
                    x: -8,
                    transition: { duration: 0.2, ease: "easeInOut" },
                  }}
                  transition={jigglySpring}
                  sx={{ overflow: "hidden", whiteSpace: "nowrap" }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: "-0.03em",
                      fontSize: { xs: "1.05rem", md: "1.15rem" },
                    }}
                  >
                    WiYou! Tech Studios
                  </Typography>
                </Box>
              )}
            </AnimatePresence>
          </Box>
        </Box>

        {/* Desktop Navigation Links */}
        {!isMobile && (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              backgroundColor: isScrolled ? "rgba(255, 253, 250, 0.94)" : "transparent",
              border: isScrolled ? "1.5px solid rgba(139, 115, 85, 0.15)" : "none",
              p: 0.5,
              borderRadius: "999px",
              transition: "background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              backdropFilter: isScrolled ? "blur(16px)" : "none",
              WebkitBackdropFilter: isScrolled ? "blur(16px)" : "none",
            }}
          >
            <LayoutGroup id="desktopNavBarLayout">
              {/* Home */}
              <Box
                component={motion.div}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={jigglySpring}
                onClick={() => handleRouteNavigate('/')}
                sx={{
                  position: "relative",
                  px: 2,
                  py: 0.7,
                  borderRadius: "999px",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {activeDesktopNavId === 'home' && (
                  <motion.div
                    layoutId="desktopNavPill"
                    transition={bounceSpring}
                    style={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 999,
                      backgroundColor: BROWN_THEME_PALETTE.primary,
                      zIndex: 0,
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  fontWeight={activeDesktopNavId === 'home' ? 700 : 500}
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    color: activeDesktopNavId === 'home' ? "#FDFBF7" : BROWN_THEME_PALETTE.secondary,
                    fontSize: "0.9rem",
                  }}
                >
                  HOME
                </Typography>
              </Box>

              {/* Services (Direct Route to /projects, no dropdown) */}
              <Box
                component={motion.div}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={jigglySpring}
                onClick={() => handleRouteNavigate('/projects')}
                sx={{
                  position: "relative",
                  px: 2,
                  py: 0.7,
                  borderRadius: "999px",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {activeDesktopNavId === 'services' && (
                  <motion.div
                    layoutId="desktopNavPill"
                    transition={bounceSpring}
                    style={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 999,
                      backgroundColor: BROWN_THEME_PALETTE.primary,
                      zIndex: 0,
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  fontWeight={activeDesktopNavId === 'services' ? 700 : 500}
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    color: activeDesktopNavId === 'services' ? "#FDFBF7" : BROWN_THEME_PALETTE.secondary,
                    fontSize: "0.9rem",
                  }}
                >
                  SERVICES
                </Typography>
              </Box>

              {/* pricing (Renamed from Projects, routes to /pricing) */}
              <Box
                component={motion.div}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={jigglySpring}
                onClick={() => handleRouteNavigate('/pricing')}
                sx={{
                  position: "relative",
                  px: 2,
                  py: 0.7,
                  borderRadius: "999px",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {activeDesktopNavId === 'pricing' && (
                  <motion.div
                    layoutId="desktopNavPill"
                    transition={bounceSpring}
                    style={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 999,
                      backgroundColor: BROWN_THEME_PALETTE.primary,
                      zIndex: 0,
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  fontWeight={activeDesktopNavId === 'pricing' ? 700 : 500}
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    color: activeDesktopNavId === 'pricing' ? "#FDFBF7" : BROWN_THEME_PALETTE.secondary,
                    fontSize: "0.9rem",
                  }}
                >
                  PRICING
                </Typography>
              </Box>

              {/* About (Dropdown with Water Pill Drop Effect) */}
              <Box
                component={motion.div}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={jigglySpring}
                onMouseEnter={(e) => handleMenuMouseEnter('about', e.currentTarget)}
                onMouseLeave={handleMenuMouseLeave}
                onClick={() => handleRouteNavigate('/about')}
                sx={{
                  position: "relative",
                  px: 2,
                  py: 0.7,
                  borderRadius: "999px",
                  cursor: "pointer",
                  userSelect: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.4,
                  backgroundColor: hoveredMenu === 'about' && activeDesktopNavId !== 'about'
                    ? "rgba(139, 115, 85, 0.1)"
                    : "transparent",
                }}
              >
                {activeDesktopNavId === 'about' && (
                  <motion.div
                    layoutId="desktopNavPill"
                    transition={bounceSpring}
                    style={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 999,
                      backgroundColor: BROWN_THEME_PALETTE.primary,
                      zIndex: 0,
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  fontWeight={activeDesktopNavId === 'about' ? 700 : 500}
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    color: activeDesktopNavId === 'about' ? "#FDFBF7" : BROWN_THEME_PALETTE.secondary,
                    fontSize: "0.9rem",
                  }}
                >
                  ABOUT
                </Typography>
                <KeyboardArrowDownIcon
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    fontSize: 16,
                    color: activeDesktopNavId === 'about' ? "#FDFBF7" : BROWN_THEME_PALETTE.secondary,
                    transform: hoveredMenu === 'about' ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  }}
                />
              </Box>
            </LayoutGroup>
          </Stack>
        )}

        {/* Desktop Mega Poppers */}
        {renderDesktopMegaPopper(
          hoveredMenu === 'about',
          aboutAnchorEl,
          'About Us',
          'Learn more about WiYou! Tech Studios',
          ABOUT_ITEMS,
          '/about',
          'about'
        )}

        {/* Right Header Action Items */}
        <Stack direction="row" spacing={1.2} alignItems="center">
          {!isMobile && (
            <Button
              component={motion.button}
              whileHover={{ scale: 1.04, y: -1 }}
              whileTap={{ scale: 0.96 }}
              transition={jigglySpring}
              variant="contained"
              onClick={() => handleRouteNavigate('/work-together')}
              startIcon={<HandshakeIcon sx={{ fontSize: "1.1rem !important" }} />}
              sx={{
                borderRadius: "999px",
                px: 2.2,
                py: 0.8,
                backgroundColor: BROWN_THEME_PALETTE.primary,
                color: "#FDFBF7",
                fontWeight: 700,
                fontSize: "0.85rem",
                textTransform: "none",
                boxShadow: 'none',
                "&:hover": {
                  backgroundColor: "#2C1D11",
                },
              }}
            >
              Work Together
            </Button>
          )}

          <Button
            component={motion.button}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            transition={jigglySpring}
            variant="outlined"
            size="small"
            onClick={() => setBottomNavOpen(true)}
            sx={{
              backgroundColor: "rgba(139, 115, 85, 0.16)",
              border: "0px solid rgba(139, 115, 85, 0.4)",
              color: BROWN_THEME_PALETTE.primary,
              px: { xs: 1.8, md: 2.2 },
              py: 0.7,
              display: { xs: "flex", md: "none" },
              borderRadius: "999px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.85rem",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              "&:hover": {
                backgroundColor: "rgba(139, 115, 85, 0.1)",
                borderColor: BROWN_THEME_PALETTE.primary,
              },
            }}
          >
            Menu
          </Button>
        </Stack>
      </Box>

      {/* Swipeable Drawer for Mobile View */}
      <SwipeableDrawer
        anchor="bottom"
        open={bottomNavOpen && isMobile}
        onClose={() => setBottomNavOpen(false)}
        onOpen={() => setBottomNavOpen(true)}
        disableSwipeToOpen={false}
        ModalProps={{
          BackdropProps: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              backdropFilter: "blur(12px)",
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: "#FFFDF0",
            border: "1.5px solid rgba(139, 115, 85, 0.2)",
            boxShadow: '0 20px 40px rgba(90, 70, 50, 0.2)',
            m: 1,
            p: 2.5,
            pb: 4,
            maxHeight: "90vh",
            overflowY: "auto",
            color: BROWN_THEME_PALETTE.textPrimary,
          },
        }}
      >
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
          {/* Drag Handle */}
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 10,
              backgroundColor: "rgba(139, 115, 85, 0.3)",
              mx: "auto",
              mb: 2,
            }}
          />

          {/* Home */}
          <ListItem
            button
            component={motion.div}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            transition={jigglySpring}
            onClick={() => handleRouteNavigate('/')}
            sx={{
              px: 2,
              py: 1.1,
              borderRadius: "14px",
              mb: 1.2,
              backgroundColor: location.pathname === '/' ? BROWN_THEME_PALETTE.accent : "rgba(139, 115, 85, 0.04)",
              border: `1.5px solid ${location.pathname === '/' ? BROWN_THEME_PALETTE.accent : "rgba(139, 115, 85, 0.15)"}`,
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: BROWN_THEME_PALETTE.primary }}>
              <HomeOutlinedIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary="Home"
              primaryTypographyProps={{
                fontSize: '0.92rem',
                fontWeight: location.pathname === '/' ? 700 : 600,
                color: BROWN_THEME_PALETTE.primary,
              }}
            />
          </ListItem>

          {/* Services (Direct Route) */}
          <ListItem
            button
            component={motion.div}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            transition={jigglySpring}
            onClick={() => handleRouteNavigate('/projects')}
            sx={{
              px: 2,
              py: 1.1,
              borderRadius: "14px",
              mb: 1.2,
              backgroundColor: location.pathname === '/projects' ? BROWN_THEME_PALETTE.accent : "rgba(139, 115, 85, 0.04)",
              border: `1.5px solid ${location.pathname === '/projects' ? BROWN_THEME_PALETTE.accent : "rgba(139, 115, 85, 0.15)"}`,
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: BROWN_THEME_PALETTE.primary }}>
              <DesignServicesIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary="Services"
              primaryTypographyProps={{
                fontSize: '0.92rem',
                fontWeight: location.pathname === '/projects' ? 700 : 600,
                color: BROWN_THEME_PALETTE.primary,
              }}
            />
          </ListItem>

          {/* pricing (Renamed from Projects, routes to /pricing) */}
          <ListItem
            button
            component={motion.div}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            transition={jigglySpring}
            onClick={() => handleRouteNavigate('/pricing')}
            sx={{
              px: 2,
              py: 1.1,
              borderRadius: "14px",
              mb: 1.2,
              backgroundColor: location.pathname === '/pricing' ? BROWN_THEME_PALETTE.accent : "rgba(139, 115, 85, 0.04)",
              border: `1.5px solid ${location.pathname === '/pricing' ? BROWN_THEME_PALETTE.accent : "rgba(139, 115, 85, 0.15)"}`,
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: BROWN_THEME_PALETTE.primary }}>
              <LocalOfferOutlinedIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary="Pricing"
              primaryTypographyProps={{
                fontSize: '0.92rem',
                fontWeight: location.pathname === '/pricing' ? 700 : 600,
                color: BROWN_THEME_PALETTE.primary,
              }}
            />
          </ListItem>

          {/* About (Always Open & Directly Clickable Header) */}
          <Box sx={{ mb: 1.5 }}>
            <Box
              component={motion.div}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              transition={jigglySpring}
              onClick={() => handleRouteNavigate('/about')}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 1.1,
                borderRadius: "14px",
                backgroundColor: isAboutActive ? BROWN_THEME_PALETTE.accent : "rgba(139, 115, 85, 0.04)",
                border: `1.5px solid ${isAboutActive ? BROWN_THEME_PALETTE.accent : "rgba(139, 115, 85, 0.15)"}`,
                cursor: "pointer",
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <InfoOutlinedIcon sx={{ fontSize: 20, color: BROWN_THEME_PALETTE.primary }} />
                <Typography sx={{ color: BROWN_THEME_PALETTE.primary, fontWeight: 700, fontSize: "0.92rem" }}>
                  About
                </Typography>
              </Stack>
              <ArrowForwardRoundedIcon sx={{ fontSize: 16, color: BROWN_THEME_PALETTE.primary }} />
            </Box>

            {/* Always Visible About Sub-Items */}
            <Box 
              sx={{ 
                mt: 0.8, 
                borderRadius: "18px", 
                border: "1.5px solid rgba(139, 115, 85, 0.15)", 
                backgroundColor: "rgba(139, 115, 85, 0.02)", 
                overflow: "hidden" 
              }}
            >
              {ABOUT_ITEMS.map((item, idx) => {
                const isItemActive = location.pathname === item.path;
                return (
                  <Box key={idx}>
                    <Box
                      component={motion.div}
                      whileHover={{ scale: 1.02, x: 4 }}
                      whileTap={{ scale: 0.97 }}
                      transition={jigglySpring}
                      onClick={() => handleRouteNavigate(item.path)}
                      sx={{
                        p: 1.4,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor: isItemActive ? "rgba(139, 115, 85, 0.12)" : "transparent",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                        <Typography sx={{ fontSize: "1.1rem" }}>{item.icon}</Typography>
                        <Box>
                          <Typography sx={{ fontSize: "0.85rem", fontWeight: isItemActive ? 700 : 600, color: BROWN_THEME_PALETTE.primary }}>
                            {item.title}
                          </Typography>
                          <Typography sx={{ fontSize: "0.74rem", color: BROWN_THEME_PALETTE.secondary }}>
                            {item.mobileSub}
                          </Typography>
                        </Box>
                      </Box>
                      <ArrowForwardRoundedIcon sx={{ fontSize: 16, color: BROWN_THEME_PALETTE.secondary }} />
                    </Box>
                    {idx < ABOUT_ITEMS.length - 1 && <Divider sx={{ borderColor: "rgba(139, 115, 85, 0.1)" }} />}
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Divider sx={{ my: 1.5, borderColor: "rgba(139, 115, 85, 0.2)" }} />

          {/* Mobile Action Buttons */}
          <Stack spacing={1.2} sx={{ mb: 1.5 }}>
            <Button
              component={motion.button}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              transition={jigglySpring}
              size="small"
              fullWidth
              variant="contained"
              startIcon={<HandshakeIcon sx={{ fontSize: 17 }} />}
              onClick={() => handleRouteNavigate('/work-together')}
              sx={{
                color: '#FDFBF7',
                borderRadius: "14px",
                py: 1.2,
                textTransform: 'none',
                fontWeight: 700,
                backgroundColor: BROWN_THEME_PALETTE.primary,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#2C1D11',
                },
              }}
            >
              Work Together
            </Button>
          </Stack>

          <Divider sx={{ my: 2, borderColor: "rgba(139, 115, 85, 0.2)" }} />

          {/* Bottom Terms & Privacy Row */}
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            sx={{ pt: 0.5 }}
          >
            <MuiLink
              component="button"
              onClick={() => handleRouteNavigate('/terms')}
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                color: BROWN_THEME_PALETTE.secondary,
                fontSize: "0.78rem",
                fontWeight: 600,
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": { color: BROWN_THEME_PALETTE.primary },
              }}
            >
              <GavelOutlinedIcon sx={{ fontSize: 14 }} />
              Terms of Service
            </MuiLink>

            <Typography sx={{ color: "rgba(139, 115, 85, 0.4)", fontSize: "0.78rem" }}>
              •
            </Typography>

            <MuiLink
              component="button"
              onClick={() => handleRouteNavigate('/privacy')}
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                color: BROWN_THEME_PALETTE.secondary,
                fontSize: "0.78rem",
                fontWeight: 600,
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": { color: BROWN_THEME_PALETTE.primary },
              }}
            >
              <SecurityOutlinedIcon sx={{ fontSize: 14 }} />
              Privacy Policy
            </MuiLink>
          </Stack>
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default Navbar;